import type { ReactNode } from 'react';

interface ArtifactWorkspaceLayoutProps {
  /** Left-column islands ("About this artifact"). */
  leftIslands: ReactNode;
  /** Center column — body section + activity feed. */
  centerContent: ReactNode;
  /** Right-column islands ("Actions and commands"). */
  rightIslands: ReactNode;
}

/**
 * Three-column workspace shell for the artifact detail page.
 *
 * - At `lg+` viewports: three independently-scrolling columns
 *   (left ≈ 280-320px, center fluid, right ≈ 400-420px). Each column owns its
 *   own scroll surface; the outer container is `flex-1 min-h-0 overflow-hidden`
 *   so the page header bar stays pinned.
 * - Below `lg`: side columns hide and their content stacks below the center
 *   column in a single scroll surface (center first, then left, then right —
 *   keeps the body editor visible at the top of the view).
 */
export function ArtifactWorkspaceLayout({
  leftIslands,
  centerContent,
  rightIslands,
}: ArtifactWorkspaceLayoutProps): React.ReactElement {
  return (
    <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
      {/* Desktop: 3-column grid with independent scrolling. */}
      <div className="hidden flex-1 min-h-0 lg:grid lg:grid-cols-[280px_minmax(0,1fr)_400px] xl:grid-cols-[320px_minmax(0,1fr)_420px]">
        <aside
          data-slot="artifact-left-column"
          aria-label="About this artifact"
          className="flex min-h-0 flex-col border-r border-border bg-surface/30"
        >
          <div className="flex-1 min-h-0 space-y-3 overflow-y-auto p-3">{leftIslands}</div>
        </aside>
        <div
          data-slot="artifact-center-column"
          className="min-h-0 space-y-4 overflow-y-auto px-4 py-4"
        >
          {centerContent}
        </div>
        <aside
          data-slot="artifact-right-column"
          aria-label="Actions and commands"
          className="flex min-h-0 flex-col border-l border-border bg-surface/30"
        >
          <div className="flex-1 min-h-0 space-y-3 overflow-y-auto p-3">{rightIslands}</div>
        </aside>
      </div>
      {/* Mobile / narrow: single scroll container, center → left → right. */}
      <div className="flex flex-1 min-h-0 flex-col overflow-y-auto lg:hidden">
        <div data-slot="artifact-center-column" className="flex flex-col gap-4 px-4 py-4">
          {centerContent}
        </div>
        <div
          data-slot="artifact-left-column"
          aria-label="About this artifact"
          className="flex flex-col gap-3 border-t border-border bg-surface/30 p-3"
        >
          {leftIslands}
        </div>
        <div
          data-slot="artifact-right-column"
          aria-label="Actions and commands"
          className="flex flex-col gap-3 border-t border-border bg-surface/30 p-3"
        >
          {rightIslands}
        </div>
      </div>
    </div>
  );
}
